import React, { memo } from 'react';

import { usePreviewPanel } from '../../contexts/PreviewPanelContext';

/**
 * ProductGridView - Grid view of products in the preview panel
 *
 * Displays products in a responsive grid layout, allowing users to browse
 * all products from a conversation. Clicking a product opens its detail view.
 */
const ProductGridView = memo(function ProductGridView({ products = [] }) {
  const { openProduct, title } = usePreviewPanel();

  if (products.length === 0) {
    return (
      <div className="p-6 text-center text-penta-grey-medium">
        No products to display
      </div>
    );
  }

  return (
    <div className="p-4">
      {title && (
        <p className="text-sm text-penta-grey-medium mb-4">{title}</p>
      )}
      <div className="grid grid-cols-2 gap-3">
        {products.map((product) => (
          <ProductGridCard
            key={product.id || product.productId}
            product={product}
            onClick={() => openProduct(product.id || product.productId, product)}
          />
        ))}
      </div>
    </div>
  );
});

/**
 * ProductGridCard - Individual product card in the grid
 */
const ProductGridCard = memo(function ProductGridCard({ product, onClick }) {
  const CONTROL_PLANE_URL = 'https://agentic-commerce-control-plane.pages.dev';

  const resolveImageUrl = (imageUrl) => {
    if (!imageUrl) return '/images/placeholder.svg';
    if (imageUrl.startsWith('/assets/')) {
      return `${CONTROL_PLANE_URL}${imageUrl}`;
    }
    return imageUrl;
  };

  const formatPrice = (price) => {
    if (!price) return '';
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    return `£${numPrice.toFixed(2)}`;
  };

  return (
    <button
      onClick={onClick}
      className="text-left bg-white border border-neutral-200 rounded-lg overflow-hidden hover:border-penta-mint hover:shadow-sm transition-all"
    >
      <div className="aspect-square relative bg-neutral-50">
        <img
          src={resolveImageUrl(product.image)}
          alt={product.title || 'Product'}
          className="absolute inset-0 w-full h-full object-cover"
          loading="lazy"
        />
      </div>
      <div className="p-2">
        {product.brand && (
          <p className="text-[10px] text-penta-grey-medium uppercase tracking-wide truncate">
            {product.brand}
          </p>
        )}
        <p className="text-xs font-medium text-penta-black line-clamp-2 leading-tight mt-0.5">
          {product.title}
        </p>
        {product.price && (
          <p className="text-sm font-semibold text-penta-black mt-1">
            {formatPrice(product.price)}
          </p>
        )}
      </div>
    </button>
  );
});

export default ProductGridView;
